/**
 * Mirrors the app's total unread count (chat messages + notifications) onto the
 * desktop app icon as a red count bubble — the taskbar overlay on Windows, the
 * Dock/launcher badge on macOS/Linux.
 *
 * Windows has no numeric taskbar badge, only a corner "overlay icon", so we
 * render the bubble to a small PNG on a canvas here and hand it to the Rust
 * `set_taskbar_badge` command. macOS/Linux draw the number natively from the
 * count, so the PNG we pass is simply ignored there.
 */
import { useEffect } from 'react';
import { invoke, isTauri as coreIsTauri } from '@tauri-apps/api/core';
import { useChatStore, selectTotalUnread } from '../store/chatStore';
import { useBadgeStore } from '../store/badgeStore';

/**
 * The bubble is RED, and deliberately not the app accent.
 *
 * It used to read `--accent-rgb` and fall back to this only when that was
 * missing, so the badge was actually purple — every comment around it said
 * "red" and none of them was true (owner, 23.09.2026).
 *
 * Fixed rather than themed: this is painted onto the TASKBAR, over whatever
 * wallpaper and taskbar tint the user has, next to every other app's unread
 * badge. Red is what that slot means everywhere else on the machine, and the
 * accent is a colour chosen to sit inside Apiboo's own window.
 */
export const BADGE_FILL = '#ef4444';

// Draw the unread bubble (red circle + white count) to a 32×32 transparent PNG
// and return its base64 body (no data: prefix). Windows downscales it to the
// 16×16 taskbar overlay; rendering at 2× keeps it crisp.
function renderBadgePng(count) {
  const S = 32;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const label = count > 99 ? '99+' : String(count);

  // Inset well inside the overlay slot so the bubble reads as a small badge
  // rather than filling the whole corner.
  const c = S / 2;
  const r = c - 3;
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fillStyle = BADGE_FILL;
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Shrink the font as the label gets wider so 1, 42 and 99+ all fit the circle.
  const fontPx = label.length >= 3 ? 13 : label.length === 2 ? 16 : 19;
  ctx.font = `600 ${fontPx}px -apple-system, "Segoe UI", system-ui, sans-serif`;
  ctx.fillText(label, c, c + 1);

  return canvas.toDataURL('image/png').split(',')[1] || null;
}

export function useTaskbarBadge() {
  const chatUnread = useChatStore(selectTotalUnread);
  const notifUnread = useBadgeStore((s) => s.notifUnread);
  const total = chatUnread + notifUnread;

  useEffect(() => {
    if (!coreIsTauri()) return;
    const icon = total > 0 ? renderBadgePng(total) : null;
    invoke('set_taskbar_badge', { count: total, iconPngBase64: icon }).catch(() => {});
  }, [total]);
}
